// web/src/billing/TrialBanner.tsx
//
// Top-of-dashboard informational banner during trial. Dismissible per
// session via localStorage. Subtle gray when ≥3 days left, amber when
// <3 days. Shown alongside (or above) PastDueBanner.
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { useSubscription } from "./api";


const DISMISS_KEY = "tapelens.trial_banner.dismissed_at";
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000; // 1 day


function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short", day: "numeric",
  });
}


function isDismissed(): boolean {
  try {
    const raw = window.localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    return Date.now() - Number(raw) < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}


export function TrialBanner() {
  const sub = useSubscription();
  const [dismissed, setDismissed] = useState(isDismissed);

  useEffect(() => {
    if (sub.data?.status !== "trialing") setDismissed(false);
  }, [sub.data?.status]);

  if (!sub.data) return null;
  if (sub.data.status !== "trialing") return null;
  if (!sub.data.trial_ends_at) return null;
  if (dismissed) return null;

  const daysLeft = Math.max(
    0,
    Math.ceil((new Date(sub.data.trial_ends_at).getTime() - Date.now()) / 86_400_000),
  );
  const urgent = daysLeft < 3;

  function dismiss() {
    try {
      window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {}
    setDismissed(true);
  }

  return (
    <div
      className={`flex items-center justify-between px-4 py-2 text-sm ${
        urgent ? "bg-amber-100 text-amber-900" : "bg-muted"
      }`}
    >
      <div>
        <span className="font-medium">
          {daysLeft === 0
            ? "Your trial ends today."
            : `Trial ends ${fmtDate(sub.data.trial_ends_at)}`}
        </span>{" "}
        <Link to="/account/billing" className="underline">
          Manage subscription
        </Link>
      </div>
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        className="text-muted-foreground hover:text-foreground"
      >
        <X className="size-4" />
      </button>
    </div>
  );
}
